import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule, DOCUMENT, isPlatformServer } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  PLATFORM_ID,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { WINDOW } from '@personal-area/common';
import { Subject } from 'rxjs';
import { ButtonComponent } from '../button/button.component';

@Component({
  selector: 'pac-filters',
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ButtonComponent,
  ],
})
export class FiltersComponent implements OnDestroy, OnChanges {
  private readonly _platformId = inject(PLATFORM_ID);

  @Input() isOpened: boolean = false;
  @Input() titleKey!: string;
  @Input() buttonKey!: string;
  @Output() resetFilter: EventEmitter<void>;
  @Output() toggleFilters: EventEmitter<void>;
  @ViewChild('filterBody') filterBody!: ElementRef;
  // this element is a container for mobile wrappers, the one without backdrop
  @ViewChild('filterContainer') filterContainer!: ElementRef<HTMLDivElement>;

  private _ngUnsubscribe: Subject<void>;

  constructor(
    protected _breakpointObserver: BreakpointObserver,
    @Inject(WINDOW) private _window: Window,
    @Inject(DOCUMENT) private _document: Document,
    private _renderer: Renderer2,
  ) {
    this.resetFilter = new EventEmitter<void>();
    this.toggleFilters = new EventEmitter<void>();
    this._ngUnsubscribe = new Subject();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (isPlatformServer(this._platformId)) {
      return;
    }

    if (!this._breakpointObserver.isMatched('(min-width: 768px)')) {
      if (changes['isOpened']?.currentValue === true) {
        const doc = this._window.document.documentElement;
        doc.style.setProperty('--modal-height', `${this._window.innerHeight}px`);
        this._window.scrollTo({ top: 0 });
        this._renderer.addClass(this._window.document.body, 'mobile-no-overflow');
        this._attachGestureListeners();
      }

      if (changes['isOpened']?.currentValue === false) {
        this._renderer.removeClass(this._window.document.body, 'mobile-no-overflow');
        this._detachGestureListeners();
      }
    }
  }

  ngOnDestroy(): void {
    this._ngUnsubscribe.next();
    this._ngUnsubscribe.complete();
  }

  private _clickOutside = (e: Event) => {
    if (this.isOpened) {
      const clickedOnBackdrop: boolean =
        !this.filterContainer.nativeElement.contains(e.target as Node) &&
        this.filterBody.nativeElement.contains(e.target);

      if (clickedOnBackdrop) {
        this.toggleFilters.emit();
        this._detachGestureListeners();
      }
    }
  };

  private _attachGestureListeners = () => {
    this._document.addEventListener('click', this._clickOutside);
  };

  private _detachGestureListeners = () => {
    this._document.removeEventListener('click', this._clickOutside);
  };
}
